import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  Phone, CalendarCheck, Snowflake, Home as HomeIcon, Building2,
  AlarmClock, ShieldCheck, BadgeCheck, ArrowRight, MapPin,
  Wrench, Clock, Mountain,
} from "lucide-react";
import { Reveal, MaskedLines, Marquee } from "@/components/Reveal";
import ServiceMap from "@/components/ServiceMap";
import LeadForm from "@/components/LeadForm";
import { PHONE_DISPLAY, PHONE_TEL, CITIES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const HoursPill = () => {
  const [after, setAfter] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setAfter((a) => !a), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <button
      onClick={() => setAfter((a) => !a)}
      data-testid="hours-pill-toggle"
      className="group flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-3 backdrop-blur transition-colors hover:border-bear-cyan/60"
    >
      <Clock className="h-4 w-4 shrink-0 text-bear-gold" />
      <AnimatePresence mode="wait">
        <motion.span
          key={after ? "after" : "std"}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -12, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="font-head text-xs font-semibold uppercase tracking-[0.14em] text-white/85"
        >
          {after
            ? "After-Hours 5 – 9 PM & Weekends"
            : "Standard Hours 7:30 AM – 5 PM"}
        </motion.span>
      </AnimatePresence>
      <span className="pulse-dot h-2 w-2 shrink-0 rounded-full bg-bear-cyan" />
    </button>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-bear-ink text-white">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 60% 45% at 50% 12%, rgba(0,168,225,0.16), transparent 70%)" }}
      />
      <div className="pointer-events-none absolute inset-x-0 top-16 flex select-none flex-col items-center text-center font-display uppercase leading-[0.85] text-stroke-ghost opacity-70">
        <span className="block text-[16vw] lg:text-[10rem]">Big</span>
        <span className="block text-[16vw] lg:text-[10rem]">Bear</span>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2, duration: 1, ease: EASE }}
        className="pointer-events-none absolute right-[15%] top-[14%] z-10 hidden w-[340px] lg:block xl:right-[16%] xl:w-[430px]"
      >
        <motion.img
          src="/images/condenser-unit.png"
          alt="Modern outdoor HVAC condenser unit"
          data-testid="hero-condenser-art"
          animate={{ y: [0, -18, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="w-full object-contain drop-shadow-[0_45px_55px_rgba(0,0,0,0.55)]"
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 sm:pb-48 sm:pt-24 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-8 flex items-center gap-3"
        >
          <Mountain className="h-4 w-4 text-bear-gold" />
          <span className="font-head text-xs font-bold uppercase tracking-[0.32em] text-bear-gold">
            Manteca, CA — Central California
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.92] tracking-wide">
          <MaskedLines
            delay={0.15}
            lines={[
              <span key="a" className="text-[clamp(1.1rem,3vw,1.75rem)] font-head font-semibold normal-case tracking-[0.06em] text-white/70">
                Central California's
              </span>,
            ]}
          />
          <MaskedLines
            delay={0.3}
            lineClassName="text-[clamp(2.6rem,8.6vw,7.4rem)]"
            lines={[
              <span key="b">Trusted</span>,
              <span key="c" className="text-bear-cyan">Heating &amp; Air</span>,
              <span key="d">Experts<span className="text-bear-gold">.</span></span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
        >
          Residential heating &amp; cooling. Dedicated mobile home HVAC services.
          Light commercial systems. Rugged reliability with agency-grade
          precision — dispatched fast from the heart of the Valley.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            href={PHONE_TEL}
            data-testid="hero-call-cta"
            className="flex items-center gap-3 rounded-full bg-bear-red px-8 py-4 font-head text-base font-bold text-white shadow-[0_16px_40px_rgba(229,36,37,0.4)]"
          >
            <Phone className="h-5 w-5" />
            Call {PHONE_DISPLAY}
          </motion.a>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/contact"
              data-testid="hero-schedule-cta"
              className="flex items-center gap-3 rounded-full border border-bear-cyan px-8 py-4 font-head text-base font-bold text-bear-cyan transition-colors hover:bg-bear-cyan hover:text-bear-ink"
            >
              <CalendarCheck className="h-5 w-5" />
              Schedule Service
            </Link>
          </motion.div>
          <HoursPill />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 1 }}
          className="mt-12"
          data-testid="brand-trust-bar"
        >
          <p className="font-head text-[10px] font-bold uppercase tracking-[0.3em] text-white/35">
            Servicing &amp; Installing All Major Brands
          </p>
          <div className="mt-4 grid grid-cols-4 gap-x-3 gap-y-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2.5">
            {[
              ["Carrier", "#2B82EC"],
              ["Trane", "#FF3B30"],
              ["Lennox", "#FF2D55"],
              ["Rheem", "#FF453A"],
              ["Goodman", "#38A8FF"],
              ["Coleman", "#30D158"],
              ["York", "#409CFF"],
              ["Daikin", "#00B4D8"],
            ].map(([b, c]) => (
              <span
                key={b}
                data-testid={`brand-badge-${b.toLowerCase()}`}
                className="text-center font-head text-[11px] font-extrabold uppercase tracking-[0.1em] transition-transform duration-300 hover:scale-105 sm:text-left lg:text-base"
                style={{ color: c, textShadow: `0 0 22px ${c}59` }}
              >
                {b}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 1 }}
          className="mt-8 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8 sm:mt-10"
        >
          {[
            ["60-MI", "Service Radius"],
            ["5–9 PM", "After-Hours, M–F"],
            ["100%", "Mobile-Home Fluent"],
          ].map(([big, small]) => (
            <div key={small}>
              <p className="font-display text-2xl tracking-wide text-white sm:text-3xl">{big}</p>
              <p className="mt-1 font-head text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45">{small}</p>
            </div>
          ))}
        </motion.div>

        <motion.img
          src="/images/condenser-unit.png"
          alt="Modern outdoor HVAC condenser unit"
          data-testid="hero-condenser-art-mobile"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9, ease: EASE }}
          className="mx-auto mt-8 w-52 object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.55)] sm:w-64 lg:hidden"
        />

        <motion.section
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.9, ease: EASE }}
          aria-label="About Big Bear Heating & Air services"
          data-testid="hero-seo-card"
          className="relative z-10 mt-10 rounded-md border border-[rgba(255,255,255,0.08)] bg-white/[0.04] p-6 backdrop-blur-md sm:p-9"
        >
          <h2 className="max-w-3xl font-head text-lg font-bold leading-snug text-white sm:text-xl">
            Central California's Premier HVAC &amp; Mobile Home Air Conditioning Specialists
          </h2>
          <p className="mt-4 max-w-4xl text-sm leading-relaxed text-white/60 sm:text-base">
            Engineered for local Central Valley weather extremes, Big Bear Heating &amp; Air delivers expert residential, light commercial, and specialized mobile home HVAC services across a 60-mile radius from Manteca, CA. Whether you need emergency AC repair during a severe heatwave, a precision furnace replacement, or complex crossover and belly-duct sealing for a mobile home, our certified technicians are dispatched fast. Count on transparent upfront pricing, standard weekday hours (7:30 AM – 5:00 PM), and dedicated after-hours emergency support (5:00 PM – 9:00 PM and full weekends).
          </p>
          <div className="mt-6 grid gap-5 sm:grid-cols-3 sm:gap-8">
            <div>
              <p className="flex items-center gap-2 font-head text-xs font-bold uppercase tracking-[0.16em] text-bear-cyan">
                <span className="h-1 w-4 rounded-full bg-bear-cyan" /> Core Solutions
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/60">
                Complete AC repair, furnace replacement, seasonal tune-ups, and specialized mobile home HVAC ductwork.
              </p>
            </div>
            <div>
              <p className="flex items-center gap-2 font-head text-xs font-bold uppercase tracking-[0.16em] text-bear-gold">
                <span className="h-1 w-4 rounded-full bg-bear-gold" /> Regional Service Area
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/60">
                Full 60-mile dispatch radius spanning Manteca, Stockton, Tracy, Modesto, Lodi, Turlock, and Ripon.
              </p>
            </div>
            <div>
              <p className="flex items-center gap-2 font-head text-xs font-bold uppercase tracking-[0.16em] text-bear-red">
                <span className="h-1 w-4 rounded-full bg-bear-red" /> Emergency Dispatch
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/60">
                Rapid response available during evenings (5:00 PM – 9:00 PM) and full weekends for urgent heating &amp; cooling failures.
              </p>
            </div>
          </div>
        </motion.section>
      </motion.div>

      <motion.svg style={{ y: yBack }} viewBox="0 0 1200 320" preserveAspectRatio="none" className="absolute bottom-0 left-0 h-32 w-full sm:h-72" aria-hidden="true">
        <path d="M0 260 L90 150 L180 230 L290 110 L400 220 L520 130 L640 240 L760 120 L880 230 L990 140 L1100 240 L1200 160 L1200 320 L0 320 Z" fill="#23292E" />
      </motion.svg>
      <motion.svg style={{ y: yFront }} viewBox="0 0 1200 320" preserveAspectRatio="none" className="absolute bottom-0 left-0 h-24 w-full sm:h-52" aria-hidden="true">
        <path d="M0 300 L140 190 L260 280 L380 170 L520 290 L660 180 L800 290 L940 170 L1060 270 L1200 200 L1200 320 L0 320 Z" fill="#0E1113" />
        <path d="M380 170 L415 205 L380 195 L350 208 Z" fill="#FCFCFC" opacity="0.85" />
        <path d="M940 170 L975 205 L940 195 L912 208 Z" fill="#FCFCFC" opacity="0.85" />
      </motion.svg>
    </section>
  );
};

const PILLARS = [
  {
    icon: Snowflake,
    num: "01",
    title: "Residential HVAC",
    copy: "Repair, maintenance & full system replacement for Valley homes — sized right, installed clean, built for 100° summers.",
    accent: "cyan",
  },
  {
    icon: HomeIcon,
    num: "02",
    title: "Mobile Home Specialists",
    copy: "Mobile home HVAC is its own craft — packaged units, crossover ductwork under the home, specialized maintenance. We speak it fluently.",
    accent: "gold",
    featured: true,
  },
  {
    icon: Building2,
    num: "03",
    title: "Light Commercial",
    copy: "Rooftop units, split systems & planned maintenance for shops, offices and small facilities across the Central Valley.",
    accent: "cyan",
  },
  {
    icon: AlarmClock,
    num: "04",
    title: "After-Hours Emergency",
    copy: "Nights 5–9 PM and full weekends. When comfort can't wait, the Bear answers — real dispatch, real techs, real fast.",
    accent: "red",
  },
];

const Pillars = () => (
  <section data-testid="pillars-section" className="bg-bear-paper py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-red">
              What We Do
            </p>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-wide text-bear-ink sm:text-6xl">
              Four Pillars
              <span className="block text-stroke-ink">Of Comfort.</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="pillars-all-services-link"
            className="group flex items-center gap-2 font-head text-sm font-bold uppercase tracking-wider text-bear-ink"
          >
            All Services
            <ArrowRight className="h-4 w-4 text-bear-red transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.1}>
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ duration: 0.35, ease: EASE }}
              data-testid={`pillar-card-${p.num}`}
              className={`group relative flex h-full flex-col overflow-hidden rounded-md border p-7 ${
                p.featured
                  ? "border-bear-gold/60 bg-bear-ink text-white shadow-[0_24px_60px_rgba(161,120,78,0.25)]"
                  : "border-black/10 bg-white"
              }`}
            >
              {p.featured && (
                <span className="absolute right-4 top-4 rounded-full bg-bear-gold px-3 py-1 font-head text-[10px] font-bold uppercase tracking-[0.14em] text-bear-ink">
                  Niche Authority
                </span>
              )}
              <span
                className={`font-display text-5xl tracking-wide ${
                  p.featured ? "text-bear-gold/50" : "text-black/10"
                }`}
              >
                {p.num}
              </span>
              <p.icon
                className={`mt-6 h-8 w-8 ${
                  p.accent === "red" ? "text-bear-red" : p.accent === "gold" ? "text-bear-gold" : "text-bear-cyan"
                } transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110`}
              />
              <h3 className={`mt-4 font-head text-lg font-bold leading-snug ${p.featured ? "text-white" : "text-bear-ink"}`}>
                {p.title}
              </h3>
              <p className={`mt-3 flex-1 text-sm leading-relaxed ${p.featured ? "text-white/65" : "text-bear-slate"}`}>
                {p.copy}
              </p>
              <Link
                to="/services"
                data-testid={`pillar-link-${p.num}`}
                className={`mt-6 flex items-center gap-2 font-head text-xs font-bold uppercase tracking-[0.16em] ${
                  p.featured ? "text-bear-gold" : "text-bear-cyan"
                }`}
              >
                Explore
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MobileSpotlight = () => (
  <section data-testid="mobile-home-spotlight" className="relative overflow-hidden bg-bear-ink2 py-24 lg:py-32">
    <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 50% 60% at 80% 50%, rgba(161,120,78,0.16), transparent 65%)" }} />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
      <Reveal>
        <div className="spotlight-photo">
          <img
            src="/images/tech-condenser.jpg"
            alt="Big Bear HVAC technician inspecting a residential AC condenser unit"
            loading="lazy"
            className="aspect-[4/5] w-full rounded-md object-cover shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
            style={{ clipPath: "polygon(0 0, 100% 6%, 100% 100%, 0 94%)" }}
          />
          <div className="absolute bottom-6 left-6 rounded-md bg-bear-ink/90 px-5 py-4 backdrop-blur">
            <p className="font-display text-3xl tracking-wide text-bear-gold">MOBILE HOME</p>
            <p className="font-head text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
              HVAC Specialists
            </p>
          </div>
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-gold">
            Dedicated Niche Authority
          </p>
          <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-wide text-white sm:text-6xl">
            Mobile Home
            <span className="block text-bear-gold">HVAC, Mastered.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/60">
            Most contractors treat mobile homes as an afterthought. We built a
            specialization around them — mobile home AC and heating repair,
            complete system replacements, and the ductwork under the home.
            It's different under there, and we know exactly why.
          </p>
        </Reveal>
        <div className="mt-10 space-y-5">
          {[
            ["Mobile home AC & heating repair", "Fast, honest diagnostics on the packaged and split systems mobile homes run on."],
            ["Mobile home ductwork", "Crossover and under-home duct repair, sealing & full replacement done right."],
            ["Replacements & maintenance", "Right-sized mobile home system replacements plus specialized seasonal care."],
          ].map(([t, c], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div className="flex gap-4 border-l-2 border-bear-gold/50 pl-5">
                <div>
                  <p className="font-head text-base font-bold text-white">{t}</p>
                  <p className="mt-1 text-sm text-white/55">{c}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <Link
            to="/services"
            data-testid="spotlight-services-cta"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-bear-gold px-8 py-4 font-head text-sm font-bold uppercase tracking-wider text-bear-ink transition-transform hover:scale-105"
          >
            See Mobile Home Services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const CHAPTERS = [
  ["Local Roots", "Born and based in Manteca. Our techs live in the same neighborhoods they serve — your comfort is our reputation.", MapPin, "cyan"],
  ["Certified Technicians", "Factory-trained, background-checked, and relentless about doing it right the first time. Boot covers on, every time.", BadgeCheck, "gold"],
  ["Transparent Pricing", "The price we quote is the price you pay. Flat, upfront, in writing — before a single wrench turns.", ShieldCheck, "cyan"],
  ["Fast Dispatch", "Same-day service across the 60-mile radius, with after-hours and weekend coverage when it can't wait.", Wrench, "red"],
];

const Why = () => (
  <section data-testid="why-section" className="bg-bear-paper py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-red">
          The Manifesto
        </p>
        <h2 className="mt-4 max-w-3xl font-display text-5xl uppercase leading-[0.95] tracking-wide text-bear-ink sm:text-6xl">
          Why The Valley
          <span className="text-bear-cyan"> Calls The Bear.</span>
        </h2>
      </Reveal>
      <div className="mt-16">
        {CHAPTERS.map(([title, copy, Icon, accent], i) => (
          <Reveal key={title} delay={i * 0.06}>
            <div
              data-testid={`why-chapter-${i + 1}`}
              className="group grid items-start gap-6 border-t border-black/10 py-10 transition-colors duration-500 last:border-b hover:bg-white sm:grid-cols-[auto_1fr_auto] sm:gap-12"
            >
              <span className="font-display text-6xl tracking-wide text-black/10 transition-colors duration-500 group-hover:text-bear-cyan sm:text-7xl">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="max-w-2xl">
                <h3 className="font-head text-2xl font-bold text-bear-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-bear-slate">{copy}</p>
              </div>
              <Icon
                className={`h-10 w-10 transition-transform duration-500 group-hover:scale-110 ${
                  accent === "red" ? "text-bear-red" : accent === "gold" ? "text-bear-gold" : "text-bear-cyan"
                }`}
              />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Guarantee = () => (
  <section data-testid="guarantee-section" className="relative overflow-hidden bg-bear-red py-20 lg:py-24">
    <div className="pointer-events-none absolute -left-8 bottom-0 select-none font-display text-[16rem] leading-none text-white/10">
      BB
    </div>
    <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-10 px-4 sm:px-6">
      <Reveal>
        <h2 className="font-display text-5xl uppercase leading-[0.95] tracking-wide text-white sm:text-6xl">
          Fixed Right.
          <span className="block">Guaranteed.</span>
        </h2>
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
          {["Upfront written pricing", "Parts & labor warranty", "Licensed & insured"].map((g) => (
            <span key={g} className="flex items-center gap-2 font-head text-sm font-semibold text-white/90">
              <ShieldCheck className="h-4 w-4" /> {g}
            </span>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <motion.a
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          href={PHONE_TEL}
          data-testid="guarantee-call-cta"
          className="flex items-center gap-3 rounded-full bg-bear-ink px-8 py-4 font-head text-base font-bold text-white"
        >
          <Phone className="h-5 w-5 text-bear-red" />
          {PHONE_DISPLAY}
        </motion.a>
      </Reveal>
    </div>
  </section>
);

const LeadCapture = () => (
  <section data-testid="lead-capture-section" className="bg-bear-paper py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr]">
      <Reveal>
        <div className="rounded-md bg-bear-ink p-10 text-white lg:p-12">
          <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-cyan">
            Book In Under A Minute
          </p>
          <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-wide sm:text-6xl">
            Schedule
            <span className="block text-bear-cyan">Service.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-white/60">
            Tell us what's going on and we'll confirm your window fast. Prefer a
            human? One tap puts a real dispatcher on the line — even after hours.
          </p>
          <div className="mt-10 space-y-6">
            <a href={PHONE_TEL} data-testid="lead-capture-call" className="group flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bear-red transition-transform duration-300 group-hover:scale-110">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-head text-xl font-bold">{PHONE_DISPLAY}</span>
                <span className="text-sm text-white/50">Standard &amp; after-hours answered</span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-bear-gold/50">
                <Clock className="h-5 w-5 text-bear-gold" />
              </span>
              <span>
                <span className="block font-head text-sm font-bold">Mon–Fri 7:30 AM – 5:00 PM</span>
                <span className="text-sm text-white/50">After-hours 5–9 PM + full weekends</span>
              </span>
            </div>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <LeadForm />
      </Reveal>
    </div>
  </section>
);

const GUIDE_CHAPTERS = [
  {
    num: "01",
    title: "Residential Heating & Cooling in Manteca & the Central Valley",
    accent: "cyan",
    paras: [
      "Life in the Central Valley asks more of an HVAC system than almost anywhere in California. July afternoons in Manteca, Stockton, and Tracy routinely push past 100 degrees, while damp tule fog and Delta air make January nights feel colder than the thermometer admits. Big Bear Heating & Air repairs, maintains, and replaces residential heating and cooling systems engineered for exactly this climate — not for a brochure written somewhere else.",
      "Every service call starts with diagnostics, not guesswork. Our technicians measure static pressure, temperature split, refrigerant charge, and airflow before recommending anything. When a system reaches the end of its life, we run a full load calculation on your home — square footage, insulation, window exposure, duct condition — so the replacement is sized exactly right. An oversized system short-cycles and dies young; an undersized one never catches up in August. We install neither.",
      "From high-efficiency heat pumps and dual-fuel setups to straightforward furnace-and-AC combinations, every job carries flat upfront pricing and a workmanship warranty. Your home is the biggest investment you own; the system that keeps it livable deserves more than a guess and a handshake.",
    ],
    points: ["AC & furnace repair — all major brands", "Full replacement with load calculations", "Heat pumps & high-efficiency upgrades", "Duct sealing, airflow balancing & smart thermostats"],
  },
  {
    num: "02",
    title: "Dedicated Mobile Home HVAC Services",
    accent: "gold",
    paras: [
      "Mobile home heating and cooling is a discipline of its own, and it is the one we are proudest of. Mobile homes run packaged units or specialized split systems, tighter mechanical clearances, and crossover ductwork that travels beneath the home — a layout most residential contractors rarely touch and often misdiagnose. Big Bear technicians service these systems every week, in parks and on private lots across San Joaquin and Stanislaus counties.",
      "Our mobile home services cover the entire life of the system: fast mobile home AC repair and heating repair when comfort can't wait, complete mobile home system replacements sized to the home's real heat load, crossover and under-home ductwork repair and resealing, and specialized maintenance that keeps compact blowers and small cabinets breathing freely through a Valley summer.",
      "If a contractor has ever told you they \"don't really do mobile homes,\" call us. We do them properly — the right equipment knowledge, the right parts on the truck, and pricing explained in plain English before a single panel comes off.",
    ],
    points: ["Mobile home AC & heating repair", "Complete mobile home system replacement", "Crossover & under-home ductwork specialists", "Specialized mobile home maintenance"],
  },
  {
    num: "03",
    title: "Light Commercial Heating & Air Conditioning",
    accent: "cyan",
    paras: [
      "A sweltering showroom or a freezing office costs you customers, staff focus, and revenue by the hour. Big Bear keeps Central Valley businesses comfortable with light commercial service built around uptime: rooftop package units, split systems, and ventilation for retail spaces, offices, kitchens, and small facilities throughout our 60-mile radius.",
      "Commercial accounts get priority dispatch, plain-language written reports after every visit, and planned maintenance agreements that replace surprise breakdowns with scheduled, budgetable care. When equipment reaches end-of-life, we quote replacements in good-better-best tiers and schedule installation around your operating hours — early mornings, evenings, whatever keeps your doors open.",
    ],
    points: ["Rooftop package-unit service & replacement", "Planned maintenance with written reporting", "Priority dispatch for commercial accounts"],
  },
  {
    num: "04",
    title: "Emergency After-Hours Dispatch in Manteca & Beyond",
    accent: "red",
    paras: [
      "Comfort emergencies don't read business hours. That's why Big Bear runs after-hours dispatch Monday through Friday from 5:00 to 9:00 PM, plus full coverage all weekend. When your furnace quits on a foggy Sunday night or the AC gives out during a Saturday heat spike, you reach a real dispatcher — not a voicemail — and a certified technician rolls with a stocked truck.",
      "What counts as an emergency? No cooling during extreme heat, no heat with infants, elderly family members, or medical needs in the home, burning or electrical odors from equipment, and active leaks that threaten property. If you're unsure, call anyway — we will help you decide honestly whether you need a truck tonight or first thing in the morning, and the price you're quoted is the price you pay either way.",
    ],
    points: ["After-hours 5–9 PM, Monday–Friday", "Full weekend coverage, Saturday & Sunday", "Real dispatcher, stocked service trucks"],
  },
  {
    num: "05",
    title: "Maintenance Memberships & Seasonal Tune-Ups",
    accent: "gold",
    paras: [
      "The cheapest repair is the one that never happens. Big Bear maintenance memberships give your system two professional tune-ups a year — a cooling check before the first 100-degree week and a heating check before tule fog season — plus priority scheduling and member pricing on any repair we find.",
      "Each tune-up is a genuine inspection, not a filter swap with an invoice attached: refrigerant levels, electrical draw, capacitor health, safety controls, condensate drains, blower condition, and thermostat calibration, documented and explained in plain English. In a valley where dust, pollen, and wildfire smoke hammer filters and coils harder than almost anywhere else, twice-a-year attention pays for itself in lower bills and longer system life.",
    ],
    points: ["Two precision tune-ups per year", "Priority scheduling & member repair pricing", "Written findings after every visit"],
  },
];

const FieldGuide = () => (
  <section id="field-guide" data-testid="field-guide-section" className="bg-bear-paper py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-red">
          The Big Bear Field Guide
        </p>
        <h2 className="mt-4 max-w-4xl font-display text-5xl uppercase leading-[0.95] tracking-wide text-bear-ink sm:text-6xl">
          Heating &amp; Air,
          <span className="text-bear-cyan"> In Depth.</span>
        </h2>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-bear-slate">
          We believe an informed homeowner is our best customer. Below is how we
          think about each discipline we practice across Manteca and the
          60-mile Central Valley radius — what matters, what to watch for, and
          how we approach the work when we're on your property.
        </p>
      </Reveal>
      <div className="mt-16 space-y-0">
        {GUIDE_CHAPTERS.map((ch, i) => (
          <Reveal key={ch.num} delay={0.05}>
            <article
              data-testid={`guide-chapter-${ch.num}`}
              className="group grid gap-6 border-t border-black/10 py-12 last:border-b lg:grid-cols-[120px_1fr] lg:gap-12"
            >
              <span className={`font-display text-6xl tracking-wide transition-colors duration-500 lg:text-7xl ${
                ch.accent === "red" ? "text-black/10 group-hover:text-bear-red" : ch.accent === "gold" ? "text-black/10 group-hover:text-bear-gold" : "text-black/10 group-hover:text-bear-cyan"
              }`}>
                {ch.num}
              </span>
              <div>
                <h3 className="max-w-3xl font-head text-2xl font-bold leading-snug text-bear-ink sm:text-3xl">
                  {ch.title}
                </h3>
                <div className="mt-5 max-w-3xl space-y-4">
                  {ch.paras.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-bear-slate">{p}</p>
                  ))}
                </div>
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {ch.points.map((pt) => (
                    <li
                      key={pt}
                      className="rounded-full border border-black/10 bg-white px-4 py-2 font-head text-xs font-semibold text-bear-ink"
                    >
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FAQS = [
  {
    q: "Is mobile home air conditioning really different from a regular house system?",
    a: "Yes — genuinely different. Mobile homes typically run packaged units or specialized split systems with tighter clearances and crossover ductwork beneath the home. Parts, airflow design, and service procedures differ from site-built houses, which is why so many contractors get them wrong. Our technicians specialize in mobile home AC repair, heating repair, full replacements, ductwork, and maintenance, so nothing on your system gets diagnosed by guesswork.",
  },
  {
    q: "Do after-hours or weekend emergency calls cost more?",
    a: "After-hours service — weekday evenings 5–9 PM and weekends — carries a dispatch premium because a technician rolls outside standard hours. But you'll hear the exact flat price before we come out, and it never changes once quoted. No hourly meters running, no surprise line items on the invoice.",
  },
  {
    q: "How often should my HVAC system be serviced in the Central Valley?",
    a: "Twice a year: a cooling tune-up in spring before the first 100-degree week, and a heating tune-up in fall before fog season. Valley dust, pollen, and summer smoke load filters and coils faster than in most regions, so staying ahead of it matters more here — systems on a maintenance schedule break down less and last measurably longer.",
  },
  {
    q: "How do I prepare my AC for a Central Valley heatwave?",
    a: "Book a pre-summer tune-up, install a fresh filter, and clear at least two feet of space around the outdoor condenser. During the heatwave itself, set the thermostat a few degrees higher when you're away instead of shutting the system off — a healthy system holds temperature far more efficiently than it recovers it, and your compressor will thank you in September.",
  },
  {
    q: "How much does a full HVAC replacement cost?",
    a: "It depends on system type, efficiency rating, home size, and duct condition — anyone who quotes a number sight-unseen is guessing. That's why we start with a free, no-pressure in-home estimate and a proper load calculation, then hand you good-better-best options in writing. The quote you approve is the price you pay, period.",
  },
  {
    q: "Which cities are inside your service area?",
    a: "We cover a full 60-mile radius from our Manteca headquarters: Stockton, Tracy, Modesto, Turlock, Lodi, Oakdale, Ripon, Lathrop, Escalon, and the communities in between. Use the radius checker above, or call (209) 304-7682 and we'll confirm your address in seconds.",
  },
  {
    q: "Can I replace just the AC or just the furnace, or do both go together?",
    a: "Often you can replace one without the other — but matched systems run more efficiently and keep full manufacturer warranty coverage. We'll evaluate what you actually have and tell you honestly whether a partial replacement makes sense or whether that money works harder toward a matched set.",
  },
  {
    q: "Are your technicians licensed and insured?",
    a: "Yes. Big Bear Heating & Air is fully licensed and insured, and every technician on your property is factory-trained, certified, and background-checked. We pull permits when the job requires them and back every repair and installation with a workmanship warranty.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <section id="faq" data-testid="faq-section" className="relative overflow-hidden bg-bear-ink py-24 text-white lg:py-32">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 50% 40% at 85% 10%, rgba(0,168,225,0.12), transparent 60%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-cyan">
              Straight Answers
            </p>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-wide sm:text-6xl">
              HVAC Questions,
              <span className="block text-bear-gold">Answered.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
              The questions Valley homeowners ask us most — on mobile home
              systems, after-hours rates, tune-up timing, heatwaves, and
              replacement estimates. Yours not here? One call settles it.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="faq-call-cta"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-bear-red px-7 py-3.5 font-head text-sm font-bold text-white transition-transform hover:scale-105"
            >
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.04}>
                <div
                  data-testid={`faq-item-${i + 1}`}
                  className={`overflow-hidden rounded-md border transition-colors duration-300 ${
                    isOpen ? "border-bear-cyan/50 bg-white/10" : "border-white/10 bg-white/5"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i + 1}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-head text-sm font-bold leading-snug sm:text-base">
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                        isOpen ? "border-bear-cyan text-bear-cyan" : "border-white/20 text-white/60"
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                      >
                        <p className="px-6 pb-6 text-sm leading-relaxed text-white/65">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <Marquee
      className="border-y border-white/10 bg-bear-ink py-5"
      itemClassName="font-head text-sm font-semibold uppercase tracking-[0.24em] text-white/50"
      items={CITIES}
    />
    <Pillars />
    <ServiceMap />
    <MobileSpotlight />
    <Why />
    <FieldGuide />
    <Guarantee />
    <FAQ />
    <LeadCapture />
  </>
);

export default Home;
